import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import Button from "components/Button";

const SIZE = 30;

// Circular "+" button matching the CircleArrow style. Opens the same dropdown
// menu as the tiny plus button on the grid dates: add a goal or add a person.
export default function CirclePlus({ addGoalCb, addPersonCb }) {
  const [hover, setHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });

  const containerRef = useRef();
  const anchorRef = useRef();

  const onClickHandler = useCallback(
    (e) => {
      e.stopPropagation();

      const clickListener = (ev) => {
        if (!containerRef.current?.contains(ev.target)) {
          window.removeEventListener("mouseup", clickListener);
          setMenuOpen(false);
        }
      };

      if (!menuOpen) {
        // Anchor the portal popup just below the plus button (fixed
        // coordinates, so ancestor overflow can't clip it).
        const rect = anchorRef.current?.getBoundingClientRect();
        if (rect) setMenuPos({ x: rect.left + rect.width / 2, y: rect.bottom + 5 });
        window.addEventListener("mouseup", clickListener);
      } else {
        window.removeEventListener("mouseup", clickListener);
      }
      setMenuOpen(!menuOpen);
    },
    [menuOpen]
  );

  // The popup is fixed-positioned; close it if anything scrolls so it doesn't
  // drift away from its anchor.
  useEffect(() => {
    if (!menuOpen) return;
    const onScroll = () => setMenuOpen(false);
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [menuOpen]);

  const addGoal = useCallback(() => {
    setMenuOpen(false);
    if (addGoalCb) addGoalCb();
  }, [addGoalCb]);

  const addPerson = useCallback(() => {
    setMenuOpen(false);
    if (addPersonCb) addPersonCb();
  }, [addPersonCb]);

  return (
    <>
      <div
        ref={anchorRef}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "50%",
          background: hover ? "#ffffff44" : "#ffffff22",
          transform: hover ? "scale(1.1)" : "scale(1)",
          width: SIZE,
          height: SIZE,
          cursor: "pointer",
          userSelect: "none",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={onClickHandler}
      >
        <div style={{ fontSize: 24, fontWeight: 300, lineHeight: 1, marginTop: -4 }}>+</div>
      </div>

      {menuOpen &&
        createPortal(
          <div
            ref={containerRef}
            style={{
              position: "fixed",
              top: menuPos.y,
              left: menuPos.x,
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              minWidth: 100,
              zIndex: 10000,
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: 8,
              padding: "7px 7px",
              boxSizing: "border-box",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", rowGap: 4 }}>
              <Button text="Add goal" callback={addGoal} lightmode noborder />
              <Button text="Person joins" callback={addPerson} lightmode noborder />
            </div>
            <div
              style={{
                position: "absolute",
                top: -10,
                left: "50%",
                transform: "translateX(-50%)",
                height: 0,
                width: 0,
                borderLeft: "10px solid transparent",
                borderRight: "10px solid transparent",
                borderBottom: "10px solid white",
              }}
            />
          </div>,
          document.body
        )}
    </>
  );
}
